import { Box, Typography, Button, ButtonBase, Modal, Fade, Backdrop, IconButton } from "@mui/material";
import aboutImage from "../../assets/Backgrounds/Profile_boy.webp";
import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import "aos/dist/aos.css"; // Import AOS styles
import "./CsrCarousel.css"; // Import custom carousel styles
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LinkIcon from "@mui/icons-material/Link";

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const getYouTubeVideoId = (url) => {
  if (!url) return null;
  // Handle various YouTube URL formats
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : url; // Return match[1] if URL, or url if it's already an ID
};

const SlickArrowLeft = ({ onClick }) => (
  <div
    className="slick-arrow-csr slick-left-csr"
    onClick={onClick}
    data-aos="fade-right"
    data-aos-delay="200"
  >
    &larr;
  </div>
);

const SlickArrowRight = ({ onClick }) => (
  <div
    className="slick-arrow-csr slick-right-csr"
    onClick={onClick}
    data-aos="fade-left"
    data-aos-delay="200"
  >
    &rarr;
  </div>
);

const Csr = ({ csr = [], csrEvent = [] }) => {
  const [link, setLink] = useState(null);
  const [exonCsr, setExonCsr] = useState([]);
  const [csrImage, setCsrEmage] = useState(null);
  const [description, setDescription] = useState(null);
  const [stemEvents, setStemEvents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [currentState, setCurrentState] = useState(0);
  const [animationKey, setAnimationKey] = useState(0); // Add this key to trigger re-animation
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Derive filteredCsr upfront (no state needed)
  const filteredCsr = csr.filter((item) => item.csr_type !== "mia");

  const currentYear = new Date().getFullYear(); // 2025

  // Refs for one-time updates to avoid loops
  const hasSetLink = useRef(false);
  const hasSetDescription = useRef(false);
  const hasSetCsrImage = useRef(false);

  // Modal handlers
  const handleOpenModal = (event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  // Effect for processing events (dependencies: only props)
  useEffect(() => {
    // Refresh AOS once on mount/change (or move to handlers if preferred)
    window.AOS?.refresh();

    const filteredEvents = [];
    const exxonCsr = filteredCsr.find(
      (csr) => csr.title === "EXXONMOBIL FOUNDATION STEM AFRICA"
    );

    filteredCsr.forEach((csr) => {
      if (csr.title === "EXXONMOBIL FOUNDATION STEM AFRICA" && exxonCsr) {
        setExonCsr(exxonCsr);
        // Set image once
        if (!csrImage && !hasSetCsrImage.current) {
          setCsrEmage(exxonCsr.image);
          hasSetCsrImage.current = true;
        }
        // Process events for current year
        csrEvent.forEach((event) => {
          const eventYear = new Date(event.dateCreated).getFullYear();
          if (eventYear === currentYear && event.csrId === csr.id) {
            filteredEvents.push({ ...event });
            // Set link once (only for specific event condition)
            if (
              (event.title !== null && event.title !== "null") &&
              (event.winners === null || event.winners === "null") &&
              (event.description !== null && event.description !== "null") &&
              (event.link !== null && event.link !== "null") &&
              !hasSetLink.current
            ) {
              setLink(event.link);
              hasSetLink.current = true;
            }
          }
        });
        // Set description once
        if (!description && !hasSetDescription.current) {
          setDescription(exxonCsr.description);
          hasSetDescription.current = true;
        }
      } else {
        // For non-Exxon CSRs, set description once if not already set
        if (!description && !hasSetDescription.current) {
          setDescription(csr.description);
          hasSetDescription.current = true;
        }
      }
    });

    setStemEvents(filteredEvents);
  }, [csr, csrEvent, currentYear]); // Only prop deps

  // Effect for auto-scrolling every 15 seconds (now stable)
  useEffect(() => {
    if (filteredCsr.length > 1 && !isHovered) {
      const interval = setInterval(() => {
        goToNext();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [filteredCsr.length, isHovered]); // Safe: derived from csr prop

  // Effect to update animation key when slide changes
  useEffect(() => {
    // Increment animation key to trigger re-animation
    setAnimationKey(prev => prev + 1);
    
    // Refresh AOS to trigger animations
    setTimeout(() => {
      window.AOS?.refresh();
    }, 50); // Small delay to ensure DOM is updated
  }, [currentSlide]);

  const goToPrev = () => {
    if (filteredCsr.length > 0) {
      setCurrentSlide((prev) =>
        prev === 0 ? filteredCsr.length - 1 : prev - 1
      );
      window.AOS?.refresh();
    }
  };

  const goToNext = () => {
    if (filteredCsr.length > 0) {
      setCurrentSlide((prev) =>
        prev === filteredCsr.length - 1 ? 0 : prev + 1
      );
      window.AOS?.refresh();
    }
  };

  const dynamicCsrEvents = (csrID) => {
    return csrEvent.length > 0 ? (
      csrEvent.map((event, index) => (
        <Box key={`${event.id || index}-${animationKey}`}> {/* Add animationKey to key */}
          {" "}
          {/* Use event.id if available for better keys */}
          {event.csrId === csrID && (
            <Box
              // only open modal if there is a video link.
              onClick={ event.video_url === "null" || event.video_url === null ? () => {} : () => handleOpenModal(event) }
              sx={{
                flex: 1,
                minWidth: "300px",
                height: '100%',
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                borderRadius: "1rem",
                overflow: "hidden",
                backgroundColor: "#fff",
                '&:hover': {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
                }
              }}
              data-aos="fade-up"
              data-aos-duration="1000"
              data-aos-delay={index * 100}
            >

              <Box
                sx={{
                  width: { xs: "100%", sm: "300px", md: "400px" },
                  height: "300px",
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  borderRadius: "1rem"
                }}
                key={`${event.id}-img-${animationKey}`} // Add key with animationKey
                data-aos="zoom-in" // Changed from fade-left to zoom-in
                data-aos-duration="800" // Slightly faster zoom
                data-aos-delay={200 + index * 100}
              >
                <img
                  src={
                    event.image
                      ? `https://mindsinaction.com.na/assets/media/pictures/${event.image}`
                      : aboutImage
                  }
                  alt={event.title}
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover",
                    transition: "transform 0.5s ease" // Add smooth transition
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                />
                
                {/* Date badge overlay */}
                {event.event_date && (
                  <Box sx={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    backgroundColor: "rgba(19, 78, 156, 0.9)",
                    color: "#fff",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px"
                  }}>
                    <CalendarTodayIcon sx={{ fontSize: "14px" }} />
                    {new Date(event.dateCreated).toLocaleDateString()}
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: "300px",
                  padding: "1rem",
                }}
                key={`${event.id}-desc-${animationKey}`} // Add key with animationKey
                data-aos="zoom-in" // Changed from fade-left to zoom-in
                data-aos-duration="800" // Slightly faster zoom
                data-aos-delay={200 + index * 100}
              >
                {/* Event Title */}
                
                
                <Typography
                  dangerouslySetInnerHTML={{ 
                    __html: event.description?.substring(0, 600) + (event.description?.length > 600 ? "..." : "") || "No description available." 
                  }}
                  variant="body1"
                  color="#333"
                  sx={{
                    lineHeight: 1.6,
                    '& p': { margin: '0.5rem 0' },
                    paddingTop: '0rem'
                  }}
                />
                
                <Box sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem 0',
                  marginTop: '1rem',
                  borderTop: '.1rem dotted gray',
                }}>
                  {event.winners && event.winners !== "null" ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <EmojiEventsIcon sx={{ color: '#ffc107' }} />
                        <Typography variant="h6" sx={{
                          color: '#020202', 
                          fontStyle: 'italic', 
                          textTransform: 'uppercase',
                          fontWeight: '600'
                        }}>
                          Winners
                        </Typography>
                        
                      </Box>
                      <Typography variant="body2" sx={{
                          color: '#08722b', 
                          fontStyle: 'italic', 
                          textTransform: 'uppercase',
                          fontWeight: '600'
                      }}>
                        {event.winners}
                      </Typography>

                      
                    </Box>
                  ) : null}

                  {/* tell users to click on the event for more details if there is a video link */}
                      {event.video_url && event.video_url !== "null" ? (
                        <Typography variant="caption" sx={{
                          color: '#134e9c',
                          fontStyle: 'italic',
                          fontSize: '1.5rem',
                          marginTop: '2rem',
                          display: 'block'
                        }}>
                          Click to view.
                        </Typography>
                      ) : null}

                  {/* { event.document && event.document !== "null" ? 
                    (
                      <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '1rem',
                        }}>
                          <ButtonBase 
                            href={`https://mindsinaction.com.na/assets/document/${event.document}`} 
                            target="_blank"
                            sx={{
                              padding: '.1rem 1rem',
                              color: '#0d3a73',
                              borderRadius: '2rem',
                              fontSize: '1.1rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              '&:hover': {
                                textDecoration: 'underline',
                              }
                            }}
                          >
                            <FileDownloadOutlinedIcon />
                            Download Report
                          </ButtonBase>
                      </Box>
                    ):null
                  } */}
                  {/* {event.link && event.link !== "null" ? (
                    <ButtonBase 
                      href={`${event.link}`} 
                      target="_blank"
                      sx={{
                        padding: '.5rem 1rem',
                        backgroundColor: '#134e9c',
                        borderRadius: '1rem',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#0d3a73',
                        }
                      }}
                      onClick={(e) => e.stopPropagation()} // Prevent modal from opening when clicking link
                    >
                      <LinkIcon sx={{ fontSize: '16px', marginRight: '4px' }} />
                      Read More
                    </ButtonBase>
                  ) :null} */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      ))
    ) : (
      <Box
        sx={{
          padding: "2rem",
          textAlign: "center",
          width: { xs: "90%", sm: "80%" },
          maxWidth: "800px",
          mx: "auto",
        }}
        data-aos="fade-in"
        data-aos-delay="100"
      >
        <Typography variant="h6" color="#134e9cff">
          No events available for {currentYear}.
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
      data-aos="fade-in"
      data-aos-duration="1000"
    >

      {filteredCsr.length > 1 && <SlickArrowLeft onClick={goToPrev} />}

      {/* Carousel Section */}
      <Box
        sx={{
          padding: { xs: "10px", sm: "30px", md: "50px" },
          backgroundColor: "rgb(236,244,247)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: "center",
          width: "100%",
          minHeight: {xs: '100vh', sm: 'auto'},
          position: "relative",
          overflow: 'hidden'
        }}
      >
        {filteredCsr.length > 0 ? (
          <Box 
            className={'carousel-container'}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            
            <Box
              sx={{
                width: "100%",
                maxWidth: '1000px',
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
              }}
              data-aos="fade-up"
              data-aos-delay={10 * 100}
            >
              {filteredCsr.map(
                (
                  csrItem,
                  index // No inner filter needed
                ) => (
                  <Box
                    key={csrItem.id || index} // Use id or index for key
                    height={'auto'}
                    boxShadow={2}
                    borderRadius={'1rem'}
                    sx={{
                      display: index === currentSlide ? "flex" : "none",
                      flexDirection: "column",
                      justifyContent: "start",
                      alignItems: "center",
                      margin: { xs: "1rem 0", sm: "2rem 0" },
                      width: "100%",
                      maxWidth: '1200px',
                      overflowX: "hidden",
                      overflowY: { xs: "auto", sm: "hidden" },
                      border: '.1rem solid #fff'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: "100%",
                        height: { xs: "100px", sm: "100px", md: "100px" },
                        maxWidth: { xs: "100%", sm: "90%", md: "1200px" },
                        minWidth: "300px",
                        display: "flex",
                        flexDirection: { xs: "row", sm: "row" },
                        alignItems: "center",
                        justifyContent: { xs: "start", sm: "start" },
                        gap: { xs: "2rem", sm: "2rem" },
                        overflow: "hidden",
                        background: '#fff',
                        padding: '1rem',
                        borderBottom: '.1rem solid gray'
                      }}
                    >
                      <Box
                        position={{xs: "relative", sm: "relative"}}
                        zIndex={1}
                        top={0}
                        left={0}
                        sx={{
                          width: { xs: "70px", sm: "100px", md: "100px" },
                          height: { xs: "70px", sm: "100px", md: "100px" },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: "hidden",
                        }}
                        data-aos="fade-right"
                        data-aos-delay={100 + csrItem.id * 100}
                      >
                        <ButtonBase>
                          <img
                            src={
                              csrItem.image
                                ? `https://mindsinaction.com.na/assets/media/pictures/${csrItem.image}`
                                : aboutImage
                            }
                            alt={csrItem.title}
                            style={{ width: "100%", objectFit: "cover" }}
                          />
                        </ButtonBase>
                        
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        fontSize={{ xs: "1rem", sm: "1.5rem", md: "2rem" }}
                        color="#134e9cff"
                        sx={{
                          textAlign: { xs: "center", sm: "left" },
                        }}
                        data-aos="fade-left"
                        data-aos-delay={200 + csrItem.id * 100}
                      >
                        {csrItem.title}
                      </Typography>
                    </Box>
                    <Box
                      padding={'1rem'}
                        sx={{
                          maxWidth: "100%",
                          borderRadius: ".5rem",
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        {dynamicCsrEvents(csrItem.id)}
                    </Box>
                    
                  </Box>
                )
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              padding: "2rem",
              textAlign: "center",
              width: { xs: "90%", sm: "80%" },
              maxWidth: "800px",
              mx: "auto",
            }}
            data-aos="fade-in"
            data-aos-delay="100"
          >
            <Typography variant="h6" color="#134e9cff">
              No CSR items available.
            </Typography>
          </Box>
        )}
      </Box>

      {filteredCsr.length > 1 && <SlickArrowRight onClick={goToNext} />}

      {/* Event Details Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            borderRadius: '1rem',
            boxShadow: 24,
            overflow: 'auto',
            outline: 'none',
          }}>
            {/* Modal Header with Close Button */}
            <Box sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              backgroundColor: '#fff',
              borderBottom: '1px solid #e0e0e0',
            }}>
              <Typography variant="h5" sx={{ fontWeight: '700', color: '#134e9c' }}>
                {selectedEvent?.title || 'Event Details'}
              </Typography>
              <IconButton onClick={handleCloseModal} sx={{ color: '#666' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Modal Content */}
            {selectedEvent && (
              <Box sx={{ padding: '2rem' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: '2rem',
                }}>
                  {/* Left Column - Event Image */}
                  <Box sx={{
                    flex: 1,
                    minWidth: '300px',
                  }}>
                    <Box sx={{
                      width: '100%',
                      height: '60vh',
                      borderRadius: '1rem',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      position: 'relative',
                    }}>
                      {
                        getYouTubeVideoId(selectedEvent.video_url) ? (
                                            <Box
                                            key={selectedEvent.id}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                            data-aos="zoom-in"
                                            data-aos-delay={100}
                                            >
                                            <iframe
                                                width="100%"
                                                height="100%"
                                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedEvent.video_url)}`}
                                                title={selectedEvent.title || "YouTube video"}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                            </Box>
                                        ) : null
                      }
                      
                      {/* Date badge */}
                      {selectedEvent.dateCreated && (
                        <Box sx={{
                          position: 'absolute',
                          bottom: '10px',
                          left: '10px',
                          backgroundColor: 'rgba(19, 78, 156, 0.9)',
                          color: '#fff',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          <CalendarTodayIcon />
                          <Typography variant="body2">
                            {new Date(selectedEvent.event_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Csr;